import React from 'react';

function Home() {
  return (
    <>
      {/* Hero Section - Industrial Split Layout */}
      <header className="relative min-h-screen bg-primary overflow-hidden">
        {/* Diagonal grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `repeating-linear-gradient(
            45deg,
            transparent,
            transparent 20px,
            white 20px,
            white 21px
          )`
        }}></div>

        {/* Accent corner */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-secondary/10 transform skew-x-[-6deg] origin-top-right"></div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-16 pb-24 min-h-screen flex items-center">
          <div className="grid lg:grid-cols-12 gap-8 items-center w-full">

            {/* Left Content - 7 columns */}
            <div className="lg:col-span-7 text-center lg:text-left">
              {/* Contractor badge */}
              <div className="inline-flex items-center gap-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full pl-3 pr-5 py-2 mb-8">
                <span className="flex items-center justify-center w-6 h-6 bg-secondary rounded-full">
                  <i className="fas fa-hard-hat text-white text-xs"></i>
                </span>
                <span className="text-sm text-white/80 font-semibold tracking-wide">Built for contractors</span>
              </div>

              {/* Main headline - stacked words */}
              <h1 className="mb-8">
                <span className="block text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[0.9] tracking-tight">
                  Quote it.
                </span>
                <span className="block text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[0.9] tracking-tight mt-2">
                  Document it.
                </span>
                <span className="block text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[0.9] tracking-tight mt-2">
                  Invoice it.
                </span>
                <span className="block text-5xl md:text-6xl lg:text-7xl font-black leading-[0.9] tracking-tight mt-3 text-transparent bg-clip-text bg-gradient-to-r from-secondary via-orange-400 to-secondary">
                  Get paid.
                </span>
              </h1>

              {/* Subheadline */}
              <p className="text-lg md:text-xl text-slate-300 max-w-xl mb-10 leading-relaxed">
                The simple app that captures client signatures on-site and generates professional PDFs in seconds.
                <span className="text-white font-medium"> No more "I never agreed to that."</span>
              </p>

              {/* CTA Row */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
                <a
                  href="/login"
                  className="group relative inline-flex items-center justify-center gap-3 bg-secondary hover:bg-orange-600 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-xl shadow-secondary/30 hover:shadow-secondary/50 transition-all duration-300 hover:-translate-y-0.5"
                >
                  <span>Start Free Trial</span>
                  <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
                </a>

                <div className="flex items-center gap-3">
                  <a
                    href="#"
                    className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-4 py-3 transition-all"
                  >
                    <i className="fab fa-apple text-xl text-white"></i>
                    <div className="text-left">
                      <div className="text-[9px] text-white/50 uppercase tracking-wider leading-none">Download on</div>
                      <div className="text-sm text-white font-semibold">App Store</div>
                    </div>
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.scopeorder.app"
                    className="flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl px-4 py-3 transition-all"
                  >
                    <i className="fab fa-google-play text-lg text-white"></i>
                    <div className="text-left">
                      <div className="text-[9px] text-white/50 uppercase tracking-wider leading-none">Get it on</div>
                      <div className="text-sm text-white font-semibold">Google Play</div>
                    </div>
                  </a>
                </div>
              </div>

              {/* Stats row */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-8 pt-8 border-t border-white/10">
                <div>
                  <div className="text-3xl font-black text-white">5 min</div>
                  <div className="text-sm text-white/50">Setup time</div>
                </div>
                <div>
                  <div className="text-3xl font-black text-white">$19</div>
                  <div className="text-sm text-white/50">Per month</div>
                </div>
                <div>
                  <div className="text-3xl font-black text-secondary">7 days</div>
                  <div className="text-sm text-white/50">Free trial</div>
                </div>
              </div>
            </div>

            {/* Right Side - Phone + Feature Cards - 5 columns */}
            <div className="lg:col-span-5 relative">
              {/* Glow effect */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-secondary/20 rounded-full blur-[100px]"></div>

              <div className="relative flex flex-col items-center lg:items-end gap-6">
                {/* Main phone mockup */}
                <div className="relative">
                  <img
                    src="/images/app-job-detail.png"
                    alt="Scope Order App"
                    className="w-64 md:w-72 rounded-[2.5rem] shadow-2xl ring-1 ring-white/10"
                  />

                  {/* Floating notification */}
                  <div className="absolute -left-16 top-24 bg-white rounded-xl px-4 py-3 shadow-xl animate-pulse-slow">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <i className="fas fa-signature text-green-600"></i>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Signature captured</div>
                        <div className="text-sm font-bold text-slate-800">PDF sent to client</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick feature cards - stacked */}
                <div className="grid grid-cols-2 gap-3 w-full max-w-sm">
                  <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/10">
                    <i className="fas fa-file-invoice text-secondary text-lg mb-2"></i>
                    <div className="text-sm font-semibold text-white">Quotes</div>
                    <div className="text-xs text-white/50">Line items + PDF</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/10">
                    <i className="fas fa-signature text-secondary text-lg mb-2"></i>
                    <div className="text-sm font-semibold text-white">Signatures</div>
                    <div className="text-xs text-white/50">On-site capture</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/10">
                    <i className="fas fa-file-contract text-secondary text-lg mb-2"></i>
                    <div className="text-sm font-semibold text-white">Change Orders</div>
                    <div className="text-xs text-white/50">Document scope</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-xl p-4 border border-white/10">
                    <i className="fas fa-receipt text-secondary text-lg mb-2"></i>
                    <div className="text-sm font-semibold text-white">Invoices</div>
                    <div className="text-xs text-white/50">Email directly</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom edge */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-slate-50 to-transparent"></div>
      </header>

      {/* Social Proof Strip */}
      <section className="py-8 px-6 bg-slate-50 border-b border-slate-200">
        <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-center gap-8 text-slate-400">
          <div className="flex items-center gap-2">
            <i className="fas fa-shield-alt text-green-500"></i>
            <span className="text-sm font-medium">Secure & Encrypted</span>
          </div>
          <div className="flex items-center gap-2">
            <i className="fab fa-apple"></i>
            <i className="fab fa-android"></i>
            <span className="text-sm font-medium">iOS & Android</span>
          </div>
          <div className="flex items-center gap-2">
            <i className="fas fa-bolt text-secondary"></i>
            <span className="text-sm font-medium">PDFs in Seconds</span>
          </div>
          <div className="flex items-center gap-2">
            <i className="fas fa-headset text-primary"></i>
            <span className="text-sm font-medium">Priority Support</span>
          </div>
        </div>
      </section>

      {/* Features Section - Asymmetric Grid */}
      <section id="features" className="py-24 px-6 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          {/* Section header */}
          <div className="max-w-2xl mb-16">
            <div className="inline-flex items-center gap-2 bg-primary/5 border border-primary/10 rounded-full px-4 py-2 mb-6">
              <i className="fas fa-tools text-primary text-sm"></i>
              <span className="text-sm font-bold text-primary uppercase tracking-wider">Features</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-primary mb-4 leading-tight">
              Everything a contractor needs. Nothing you don't.
            </h2>
            <p className="text-lg text-slate-500">
              Simple tools that work on the job site. No bloat. No learning curve.
            </p>
          </div>

          {/* Feature Grid */}
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Large feature - Signature Capture */}
            <div className="lg:col-span-2 lg:row-span-2 group relative bg-primary rounded-3xl p-8 overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-secondary/10 rounded-full blur-3xl"></div>
              <div className="relative z-10 h-full flex flex-col">
                <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center mb-6">
                  <i className="fas fa-signature text-2xl text-white"></i>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">On-Site Signature Capture</h3>
                <p className="text-white/70 text-lg mb-6 max-w-md">
                  Get client approval instantly on your phone. No more chasing signatures or dealing with "I never agreed to that."
                </p>
                <div className="mt-auto">
                  <img
                    src="/images/app-review-order.png"
                    alt="Signature capture screen"
                    className="w-72 rounded-2xl shadow-2xl transform group-hover:scale-[1.02] transition-transform duration-500"
                  />
                </div>
              </div>
            </div>

            {/* Quotes */}
            <div className="group bg-white rounded-3xl p-6 shadow-sm border border-slate-100 hover:shadow-xl hover:border-secondary/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-secondary transition-colors">
                <i className="fas fa-file-invoice text-xl text-secondary group-hover:text-white transition-colors"></i>
              </div>
              <h3 className="text-xl font-bold text-primary mb-2">Professional Quotes</h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                Line-item breakdowns for labor and materials. Generate branded PDFs and email directly to clients.
              </p>
            </div>

            {/* Change Orders */}
            <div className="group bg-white rounded-3xl p-6 shadow-sm border border-slate-100 hover:shadow-xl hover:border-secondary/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-500 transition-colors">
                <i className="fas fa-file-contract text-xl text-blue-600 group-hover:text-white transition-colors"></i>
              </div>
              <h3 className="text-xl font-bold text-primary mb-2">Change Orders</h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                Document scope changes before the work begins. Get signatures that protect your bottom line.
              </p>
            </div>

            {/* Invoices */}
            <div className="group bg-white rounded-3xl p-6 shadow-sm border border-slate-100 hover:shadow-xl hover:border-secondary/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-green-500 transition-colors">
                <i className="fas fa-receipt text-xl text-green-600 group-hover:text-white transition-colors"></i>
              </div>
              <h3 className="text-xl font-bold text-primary mb-2">Sequential Invoices</h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                INV-0001, INV-0002... Professional invoice numbering with payment terms and due dates.
              </p>
            </div>

            {/* Client Management */}
            <div className="group bg-white rounded-3xl p-6 shadow-sm border border-slate-100 hover:shadow-xl hover:border-secondary/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-purple-500 transition-colors">
                <i className="fas fa-users text-xl text-purple-600 group-hover:text-white transition-colors"></i>
              </div>
              <h3 className="text-xl font-bold text-primary mb-2">Client Database</h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                Keep all your clients organized with contact info, addresses, and full job history.
              </p>
            </div>

            {/* Photo Documentation */}
            <div className="group bg-white rounded-3xl p-6 shadow-sm border border-slate-100 hover:shadow-xl hover:border-secondary/30 transition-all duration-300 hover:-translate-y-1">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-amber-500 transition-colors">
                <i className="fas fa-camera text-xl text-amber-600 group-hover:text-white transition-colors"></i>
              </div>
              <h3 className="text-xl font-bold text-primary mb-2">Photo Documentation</h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                Attach photos to jobs. Document conditions, progress, and completed work with timestamps.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 bg-green-100 rounded-full px-4 py-2 mb-6">
              <i className="fas fa-route text-green-600 text-sm"></i>
              <span className="text-sm font-bold text-green-700 uppercase tracking-wider">How It Works</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-primary mb-4">
              Three steps to getting paid
            </h2>
            <p className="text-lg text-slate-500 max-w-xl mx-auto">
              No complicated setup. Start sending quotes in under 5 minutes.
            </p>
          </div>

          {/* Steps */}
          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="relative text-center group">
              <div className="w-20 h-20 bg-white border-4 border-secondary rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform">
                <span className="text-3xl font-black text-secondary">1</span>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-paper-plane text-xl text-secondary"></i>
                </div>
                <h3 className="text-xl font-bold text-primary mb-2">Send a Quote</h3>
                <p className="text-slate-500 text-sm">
                  Create a job, add line items for labor and materials, and send a professional quote.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative text-center group">
              <div className="w-20 h-20 bg-white border-4 border-blue-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform">
                <span className="text-3xl font-black text-blue-500">2</span>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-signature text-xl text-blue-500"></i>
                </div>
                <h3 className="text-xl font-bold text-primary mb-2">Document Changes</h3>
                <p className="text-slate-500 text-sm">
                  When scope changes, capture the client's signature on-site and email the PDF instantly.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative text-center group">
              <div className="w-20 h-20 bg-white border-4 border-green-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform">
                <span className="text-3xl font-black text-green-500">3</span>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-dollar-sign text-xl text-green-500"></i>
                </div>
                <h3 className="text-xl font-bold text-primary mb-2">Bill & Get Paid</h3>
                <p className="text-slate-500 text-sm">
                  Generate a professional invoice with payment terms and email it directly to your client.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* App Screenshots */}
      <section className="py-24 px-6 bg-slate-50 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-purple-100 rounded-full px-4 py-2 mb-6">
              <i className="fas fa-mobile-alt text-purple-600 text-sm"></i>
              <span className="text-sm font-bold text-purple-700 uppercase tracking-wider">App Preview</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-primary mb-4">
              Designed for the job site
            </h2>
            <p className="text-lg text-slate-500 max-w-xl mx-auto">
              Large buttons. Simple navigation. Works with dirty hands and bright sunlight.
            </p>
          </div>

          {/* Screenshots */}
          <div className="relative">
            <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-secondary/20 rounded-full blur-[100px]"></div>
            <div className="absolute top-1/2 right-1/4 w-64 h-64 bg-blue-400/20 rounded-full blur-[100px]"></div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="group relative">
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-r from-secondary to-orange-600 rounded-[2.5rem] blur-xl opacity-30 group-hover:opacity-50 transition-opacity"></div>
                  <img
                    src="/images/app-review-order.png"
                    alt="Change Order Review"
                    className="relative w-full rounded-[2rem] shadow-2xl"
                  />
                </div>
                <div className="text-center mt-6">
                  <h3 className="text-xl font-bold text-primary mb-1">Review & Send</h3>
                  <p className="text-slate-500 text-sm">Capture signatures and send PDFs instantly</p>
                </div>
              </div>

              <div className="group relative md:-mt-8">
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-r from-blue-400 to-purple-600 rounded-[2.5rem] blur-xl opacity-30 group-hover:opacity-50 transition-opacity"></div>
                  <img
                    src="/images/app-invoice.png"
                    alt="Invoice Creation"
                    className="relative w-full rounded-[2rem] shadow-2xl ring-2 ring-white"
                  />
                </div>
                <div className="text-center mt-6">
                  <h3 className="text-xl font-bold text-primary mb-1">Create Invoices</h3>
                  <p className="text-slate-500 text-sm">Professional invoices with payment terms</p>
                </div>
              </div>

              <div className="group relative">
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-r from-green-400 to-teal-600 rounded-[2.5rem] blur-xl opacity-30 group-hover:opacity-50 transition-opacity"></div>
                  <img
                    src="/images/app-profile.png"
                    alt="Dashboard"
                    className="relative w-full rounded-[2rem] shadow-2xl"
                  />
                </div>
                <div className="text-center mt-6">
                  <h3 className="text-xl font-bold text-primary mb-1">Track Everything</h3>
                  <p className="text-slate-500 text-sm">Monitor earnings and orders at a glance</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="relative bg-primary rounded-[2.5rem] overflow-hidden">
            {/* Background effects */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl"></div>

            <div className="relative z-10 p-8 md:p-16">
              {/* Free Trial Badge */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-2 bg-green-500 text-white rounded-full px-6 py-2 font-bold text-sm shadow-lg">
                  <i className="fas fa-gift"></i>
                  7-Day Free Trial - No Credit Card Required
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-12 items-center">
                {/* Left: Features */}
                <div>
                  <h2 className="text-3xl md:text-4xl font-black text-white mb-6">
                    Everything you need.<br />
                    <span className="text-secondary">One simple price.</span>
                  </h2>

                  <ul className="space-y-4">
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="fas fa-check text-green-400 text-xs"></i>
                      </div>
                      <div>
                        <span className="text-white font-medium">Unlimited quotes, change orders & invoices</span>
                        <p className="text-white/50 text-sm">No per-document fees, ever</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="fas fa-check text-green-400 text-xs"></i>
                      </div>
                      <div>
                        <span className="text-white font-medium">Unlimited clients & jobs</span>
                        <p className="text-white/50 text-sm">Scale your business without limits</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="fas fa-check text-green-400 text-xs"></i>
                      </div>
                      <div>
                        <span className="text-white font-medium">On-site signature capture</span>
                        <p className="text-white/50 text-sm">Digital signatures on your phone</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="fas fa-check text-green-400 text-xs"></i>
                      </div>
                      <div>
                        <span className="text-white font-medium">PDF generation & email delivery</span>
                        <p className="text-white/50 text-sm">Professional documents in seconds</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="fas fa-check text-green-400 text-xs"></i>
                      </div>
                      <div>
                        <span className="text-white font-medium">Photo documentation</span>
                        <p className="text-white/50 text-sm">Attach photos to any job</p>
                      </div>
                    </li>
                  </ul>
                </div>

                {/* Right: Price Card */}
                <div className="bg-white rounded-3xl p-8 text-center shadow-2xl">
                  <div className="text-slate-500 font-medium mb-2">Pro Membership</div>
                  <div className="flex items-baseline justify-center gap-1 mb-2">
                    <span className="text-6xl font-black text-primary">$19</span>
                    <span className="text-slate-400 font-medium">/month</span>
                  </div>
                  <p className="text-slate-500 text-sm mb-8">Billed monthly. Cancel anytime.</p>

                  <a
                    href="/login"
                    className="block w-full bg-secondary hover:bg-orange-600 text-white py-4 rounded-xl font-bold text-lg shadow-lg shadow-secondary/25 hover:shadow-secondary/40 hover:-translate-y-0.5 transition-all"
                  >
                    Start Free Trial
                  </a>

                  <p className="text-slate-400 text-sm mt-4">
                    <i className="fas fa-lock text-slate-300 mr-1"></i>
                    Secure payment via Stripe
                  </p>

                  {/* Comparison */}
                  <div className="mt-8 pt-6 border-t border-slate-100">
                    <div className="text-slate-400 text-sm mb-3">Compare to alternatives:</div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between text-slate-500">
                        <span>Paper forms</span>
                        <span className="text-red-400">Hours of work</span>
                      </div>
                      <div className="flex justify-between text-slate-500">
                        <span>Enterprise software</span>
                        <span className="text-red-400">$99+/month</span>
                      </div>
                      <div className="flex justify-between font-medium">
                        <span className="text-primary">Scope Order</span>
                        <span className="text-green-500">$19/month</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6 bg-slate-50 relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-gradient-to-b from-secondary/10 to-transparent rounded-full blur-3xl"></div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-primary mb-6 leading-tight">
            Stop losing money on<br />
            <span className="text-secondary">undocumented work</span>
          </h2>
          <p className="text-xl text-slate-500 mb-10 max-w-2xl mx-auto">
            Every change order without a signature is money at risk. Start protecting your work today.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <a
              href="/login"
              className="group inline-flex items-center justify-center gap-2 bg-secondary hover:bg-orange-600 text-white px-10 py-5 rounded-xl font-bold text-lg shadow-xl shadow-secondary/25 hover:shadow-secondary/40 transition-all hover:-translate-y-0.5"
            >
              Start Your Free Trial
              <i className="fas fa-arrow-right group-hover:translate-x-1 transition-transform"></i>
            </a>
          </div>

          {/* App Store Badges */}
          <div className="flex items-center justify-center gap-4 mb-12">
            <a href="#" className="flex items-center gap-2 bg-slate-900 text-white rounded-xl px-5 py-3 hover:bg-black transition-colors">
              <i className="fab fa-apple text-2xl"></i>
              <div className="text-left">
                <div className="text-[10px] text-white/70 uppercase">Download on the</div>
                <div className="text-sm font-semibold -mt-0.5">App Store</div>
              </div>
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.scopeorder.app" className="flex items-center gap-2 bg-slate-900 text-white rounded-xl px-5 py-3 hover:bg-black transition-colors">
              <i className="fab fa-google-play text-xl"></i>
              <div className="text-left">
                <div className="text-[10px] text-white/70 uppercase">Get it on</div>
                <div className="text-sm font-semibold -mt-0.5">Google Play</div>
              </div>
            </a>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap items-center justify-center gap-6 text-slate-400 text-sm">
            <div className="flex items-center gap-2">
              <i className="fas fa-shield-alt text-green-500"></i>
              <span>Secure & Encrypted</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="fas fa-credit-card"></i>
              <span>No Credit Card Required</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="fas fa-undo"></i>
              <span>Cancel Anytime</span>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default Home;
